"use client";

/**
 * 列表卡片与首页推荐块的真 JSX 组件（取代旧 render.ts 的字符串拼接 + dangerouslySetInnerHTML）。
 * DOM 结构、class 名与 data-* 属性与改造前完全一致，只是改为 React 元素；
 * 需要跳转的卡片用 go() 走 path 段（详情页统一 ID 路径）。
 */
import { go, icons } from "./AdditionalPages";
import { cityCover } from "@/common/parks/cityCover";
import { locLine } from "@/common/parks/render";
import { isLatest } from "@/common/policies/render";
import { monthDay, statusKey, activityStatus } from "@/common/activities/render";
import { statusOf } from "@/common/invest/render";
import type { ParkItem } from "@/common/interface/park";
import type { OpcItem, OpcProduct } from "@/common/interface/opc";
import type { PolicyItem } from "@/common/interface/policy";
import type { ActivityItem } from "@/common/interface/activity";
import type { InvestOpportunity } from "@/common/interface/invest";

/** 园区卡片（列表页与首页统一使用） */
export function ParkCard({ park }: { park: ParkItem }) {
  const tags = (park.focusTracks || []).slice(0, 3);
  const points = (park.highlights || []).slice(0, 3);
  return (
    <article data-slug={park.slug} data-city={park.city} data-province={park.province}>
      <div
        className="park-grid-cover"
        style={{ ["--park-cover" as string]: `url('${cityCover(park.city, "card")}')` }}
      >
        <h2>{park.name}</h2>
        <p className="park-grid-loc">{locLine(park)}</p>
      </div>
      <div className="park-grid-body">
        <p className="park-grid-desc">{park.desc}</p>
        {tags.length > 0 && (
          <div className="park-grid-tags">{tags.map((t) => <em key={t}>{t}</em>)}</div>
        )}
        {points.length > 0 && (
          <ul className="park-grid-points">{points.map((t) => <li key={t}>{t}</li>)}</ul>
        )}
        <button onClick={() => go(`/park-detail/${park.slug}`)}>查看园区详情 →</button>
      </div>
    </article>
  );
}

/** OPC 卡片（列表页与首页共用） */
export function OpcCard({ opc, products }: { opc: OpcItem; products: OpcProduct[] }) {
  const rep = products.find((p) => p.ownerSlug === opc.slug);
  const labels = [opc.city, opc.industry].filter(Boolean);
  const tags = (opc.tags || []).filter(Boolean).slice(0, 3);
  return (
    <article className="opc-card" data-slug={opc.slug} data-city={opc.city} data-industry={opc.industry}>
      <div className="opc-head">
        <i>{icons.building}</i>
        <span>
          <h3>{opc.name}</h3>
          {opc.title && <p>{opc.title}</p>}
        </span>
      </div>
      {labels.length > 0 && (
        <div className="labels">{labels.map((v) => <span key={v}>{v}</span>)}</div>
      )}
      <p className="intro">{opc.intro}</p>
      {tags.length > 0 && <div className="skills">{tags.map((v) => <span key={v}>{v}</span>)}</div>}
      {rep && (
        <div className="product">
          <small>代表产品</small>
          <b>{rep.name}</b>
          <p>{rep.tagline}</p>
        </div>
      )}
      <button onClick={() => go(`/opc-detail/${opc.id}`)}>查看 OPC 详情 →</button>
    </article>
  );
}

/** 产品卡片（列表页与首页共用） */
export function ProductCard({ product }: { product: OpcProduct }) {
  const labels = [product.category, product.pricing].filter(Boolean);
  const tags = (product.tags || []).filter(Boolean).slice(0, 3);
  return (
    <article className="opc-card product-card" data-slug={product.slug} data-category={product.category}>
      <div className="opc-head">
        <i>{icons.box}</i>
        <span>
          <h3>{product.name}</h3>
          {product.ownerName && <p>{product.ownerName}</p>}
        </span>
      </div>
      {labels.length > 0 && (
        <div className="labels">{labels.map((v) => <span key={v}>{v}</span>)}</div>
      )}
      <p className="intro">{product.tagline}</p>
      {tags.length > 0 && <div className="skills">{tags.map((v) => <span key={v}>{v}</span>)}</div>}
      <button onClick={() => go(`/product-detail/${product.id}`)}>查看产品详情 →</button>
    </article>
  );
}

/** 政策卡片：权威出口用 <a> 直达政府官网原文 */
export function PolicyCard({ policy, now }: { policy: PolicyItem; now?: Date }) {
  return (
    <a
      data-province={policy.province}
      data-city={policy.city}
      href={policy.url}
      target="_blank"
      rel="noreferrer"
    >
      {isLatest(policy.date, now) && <b className="policy-new">最新</b>}
      <div className="policy-card-meta">
        <span>{policy.city}</span>
        <em>{policy.level}</em>
        <time>{policy.date}</time>
      </div>
      <h2>{policy.title}</h2>
      <p>{policy.summary}</p>
      <div>
        <b>{policy.issuer}</b>
        <strong>查看官方原文 ↗</strong>
      </div>
    </a>
  );
}

/** 活动卡片：权威出口用 <a> 直达官方公告原文 */
export function ActivityCard({ activity, now }: { activity: ActivityItem; now?: Date }) {
  const a = activity;
  const st = activityStatus(a, now);
  const deadline = a.applyDeadline && st === "报名中" ? `报名截止 ${monthDay(a.applyDeadline)}` : "";
  return (
    <article
      className={st === "已结束" ? "activity-ended" : ""}
      data-region={a.city}
      data-province={a.province}
      data-category={a.category}
      data-status={st}
    >
      <div className="activity-cover">
        <span>{a.city}</span>
        <b>
          {monthDay(a.startDate)}
          {a.endDate && a.endDate !== a.startDate && <u>至 {monthDay(a.endDate)}</u>}
        </b>
        <small>{a.category}</small>
        <i className={`activity-status activity-status--${statusKey(st)}`}>{st}</i>
      </div>
      <div className="activity-card-body">
        <h2>{a.title}</h2>
        <p>{a.summary}</p>
        <em>{a.venue} · {a.organizer}</em>
        {deadline && <small className="activity-deadline">{deadline}</small>}
        <a href={a.url} target="_blank" rel="noreferrer">查看官方公告 ↗</a>
      </div>
    </article>
  );
}

/** 投资机会卡片（home=true 时用于首页轮播：隐藏底部说明文案，链接置于左下角） */
export function InvestCard({ item, now, home = false }: { item: InvestOpportunity; now?: Date; home?: boolean }) {
  const status = statusOf(item, now);
  const when = item.deadline && !item.rolling
    ? `报名截至 ${item.deadline}`
    : status.text === "持续更新" ? "按官方通知动态开放" : "";
  return (
    <article data-type={item.typeLabel} data-region={item.region}>
      <div className="public-opportunity-status">
        <span>{item.typeLabel}</span>
      </div>
      <div className="public-opportunity-main">
        <h3>{item.title}</h3>
        <p>{item.issuer}</p>
        {(item.audience || item.support) && (
          <dl>
            {item.audience && <div><dt>适合对象</dt><dd>{item.audience}</dd></div>}
            {item.support && <div><dt>支持内容</dt><dd>{item.support}</dd></div>}
          </dl>
        )}
        {!home && <small>{when ? `${when} · ` : ""}信息以官方最新通知为准</small>}
      </div>
      <a href={item.url} target="_blank" rel="noreferrer">查看官方原文 ↗</a>
    </article>
  );
}
